import { Trans } from "@lingui/react/macro";
import React, { FC, useState, useCallback, useMemo } from "react";
import Modal, { BaseModalProps } from "components/Modal/Modal";
import { BrochureFragment, FormDisclaimerFragment } from "types/graphql-types";
import { ModalBody } from "@silversea-ssc/ui-components-react";
import {
  buildBrochureThankYouPageURL,
  doRedirectToThankYouPage
} from "./utils";
import RequestBrochureModalForm from "components/RequestBrochureModal/RequestBrochureModalForm";
import * as styles from "./RequestBrochureModal.module.scss";

interface NewRequestBrochureModalProps {
  isOpen: boolean;
  onAfterOpen?: () => void;
  onRequestClose: () => void;
  brochure: NonNullable<BrochureFragment>;
  thankYouStragegy: "newPage" | "redirect";
  thankYouPageURL: string;
  formDisclaimerContent: FormDisclaimerFragment | undefined;
}

const RequestBrochureModal: FC<
  React.PropsWithChildren<NewRequestBrochureModalProps>
> = ({
  isOpen,
  onAfterOpen,
  onRequestClose,
  brochure,
  thankYouPageURL,
  formDisclaimerContent,
  thankYouStragegy
}) => {
  const title = brochure.title;
  const openInNewPage = thankYouStragegy == "newPage";
  const [isPrinted, setIsPrinted] = useState(false);
  const redirectTo = useMemo(
    () =>
      brochure?.brochureCode
        ? buildBrochureThankYouPageURL(
            thankYouPageURL,
            brochure.brochureCode,
            isPrinted
          )
        : undefined,
    [brochure.brochureCode, thankYouPageURL, isPrinted]
  );

  const [isSubmitted, setIsSubmitted] = useState(false);

  const resetOnAfterOpen = useCallback(() => {
    setIsSubmitted(false);
    if (onAfterOpen) onAfterOpen();
  }, [onAfterOpen]);

  const markFormAsSubmitted = useCallback(
    (isPrinted: boolean) => {
      setIsPrinted(isPrinted);
      const redirectTo = buildBrochureThankYouPageURL(
        thankYouPageURL,
        brochure.brochureCode!,
        isPrinted
      );

      doRedirectToThankYouPage(redirectTo, openInNewPage);
      onRequestClose();
    },
    [onRequestClose, redirectTo, openInNewPage]
  );

  const modalProps: BaseModalProps = {
    isOpen: isOpen,
    onAfterOpen: resetOnAfterOpen,
    onRequestClose: onRequestClose,
    contentLabel: title
  };

  return (
    <div>
      <Modal
        title={title}
        modal={modalProps}
        size="medium"
        dataContentName="rab_modal"
      >
        {isSubmitted ? (
          <React.Fragment>
            <div style={{ textAlign: "center" }}>
              <a href={redirectTo}>Redirecting...</a>
            </div>
          </React.Fragment>
        ) : (
          <Form
            brochure={brochure}
            setIsSubmitted={markFormAsSubmitted}
            formDisclaimerContent={formDisclaimerContent}
          />
        )}
      </Modal>
    </div>
  );
};

const Form: React.FC<
  React.PropsWithChildren<{
    brochure: BrochureFragment;
    setIsSubmitted: (value: boolean) => void;
    formDisclaimerContent: FormDisclaimerFragment | undefined;
  }>
> = ({ brochure, setIsSubmitted, formDisclaimerContent }) => {
  return (
    <ModalBody variant={"wide"}>
      <div className={styles.modalTitle}>
        <Trans>Fill in the form to view online our free brochure</Trans>
      </div>
      <RequestBrochureModalForm
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        brochureCode={brochure.brochureCode!}
        onSubmitSuccess={(_, request) =>
          setIsSubmitted(request.RequestSource === "BRO")
        }
        formDisclaimerContent={formDisclaimerContent}
        formName={"RequestBrochureForm"}
        dataContentName="rab_form"
        digitalOnly={brochure.isDigitalOnly ?? true}
      />
    </ModalBody>
  );
};

export default RequestBrochureModal;
